import type { PropertyDetail } from '@/types/property'
import {
  buildPropertySeoDescription,
  buildPropertySeoKeywords,
  buildPropertySeoTitle,
  propertyCanonicalUrl,
  propertyDetailSchemas,
  resolveSeo,
  type ResolvedSeo,
} from '@/lib/seo'
import { isValidPropertyCanonical } from '@/lib/seo/utils'

/**
 * Property detail SEO:
 * 1) Admin/DB fields when filled
 * 2) Auto-generated from property data
 * 3) Invalid DB canonical (homepage etc.) is ignored
 * 4) noindex / nofollow from admin toggles
 */
export function resolvePropertyDetailSeo(property: PropertyDetail): ResolvedSeo {
  const admin = property.seo ?? undefined
  const dbCanonical = admin?.canonicalUrl?.trim() || null
  const canonical = propertyCanonicalUrl(property, dbCanonical)

  // Never let a bad DB canonical override the validated one via resolveSeo.
  const adminForResolve = admin
    ? {
        ...admin,
        canonicalUrl:
          dbCanonical && isValidPropertyCanonical(dbCanonical, property.slug)
            ? absoluteCanonical(dbCanonical)
            : null,
      }
    : undefined

  return resolveSeo(
    {
      title: buildPropertySeoTitle(property),
      description: buildPropertySeoDescription(property),
      keywords: buildPropertySeoKeywords(property),
      canonical,
      ogImage: admin?.ogImage || property.image,
      ogType: 'website',
      schema: propertyDetailSchemas(property),
      noIndex: Boolean(admin?.noIndex),
      noFollow: admin?.follow == null ? false : !admin.follow,
    },
    adminForResolve,
  )
}

function absoluteCanonical(url: string): string {
  try {
    const parsed = new URL(url)
    parsed.protocol = 'https:'
    if (/^(www\.)?qbcon\.in$/i.test(parsed.hostname)) {
      parsed.hostname = 'www.qbcon.in'
    }
    parsed.hash = ''
    return parsed.toString().replace(/\/$/, '')
  } catch {
    return url
  }
}
